import dayjs from 'dayjs';
import { DataModel, DataModelBuilder, SerializationContext, SerializedData } from '../../dto/data-model.js';
import { MapUtils } from '../../../utility/map-utils.js';
import {
  cloneData, cloneListOrNull, generateUniqueDatabaseId, isSameData, isSameList,
} from '../../dto/data-model-utils.js';
import { Product } from '../product.js';
import { TrueTime } from '../../../time/true-time.js';
import { TimeZone } from '../../../time/timezone.js';
import { BusinessModuleRef } from '../business-module-ref.js';
import { SellableVoucher, VoucherStatus } from './voucher.js';
import { ComponentList } from '../../../components/core/component-list.js';
import { fromEnumAsString, toEnumFromString } from '../../../serialization/enum-utils.js';
import { OpenTicketPassComponent } from '../../../components/voucher/open-ticket-pass-component.js';
import { isNotNullOrUndefined, isNullOrUndefined } from '../../../utility/null-safety.js';
import { Discount } from './discount.js';
import { VersionRange } from '../../core/version-range.js';
import { Price } from '../price.js';
import { OpenTicketPassType } from './open-ticket-pass-type.js';
import { Result } from '../../../utility/result.js';
import { ErrorLike } from '../../core/error-like.js';
import { JSONUtils } from '../../../utility/json-utils.js';
import { IllegalOperationException } from '../../../exceptions/illegal-operation-exception.js';
import { CustomerOrderBase } from '../../../contracts/customer-order.js';
import { VoucherAttachmentComponent } from '../../../components/voucher/voucher-attachment-component.js';

/**
 * @deprecated Use FlexiblePassProtocol of OpenAccessPass instead.
 */
export class OpenTicketPass extends SellableVoucher {
  public static readonly TYPE_ID: string = '4c344c315cf04e72b39f456213d7fb44';
  public static readonly SCHEMA_VERSION: number = 9;

  private _status: VoucherStatus;
  private _includedEvents: string[] | null;
  private _excludedEvents: string[] | null;
  private _ticketId: string[] | null;
  private _usedAt: dayjs.Dayjs | null;
  private _maxUses: number;
  private _uses: number | null;
  private _type: OpenTicketPassType;

  private constructor(
    public readonly uniqueId: string,
    public readonly businessRef: BusinessModuleRef,
    public readonly creationDate: dayjs.Dayjs,
    public readonly orderId: string,
    public readonly customerId: string,
    public readonly productId: string,
    public readonly productPrice: Price,
    public readonly components: ComponentList,
    public readonly startDate: dayjs.Dayjs | null,
    public readonly expirationDate: dayjs.Dayjs | null,
    public readonly serialNumber: string | null,
    public readonly userId: string | null,
    public readonly isGuestOrder: boolean | null,
    public readonly orderEntryId: string | null,
    includedEvents: string[] | null,
    excludedEvents: string[] | null,
    ticketId: string[] | null,
    status: VoucherStatus,
    usedAt: dayjs.Dayjs | null,
    maxUses: number,
    uses: number | null,
    type: OpenTicketPassType,
  ) {
    super();
    this._status = status;
    this._usedAt = usedAt;
    this._maxUses = maxUses;
    this._uses = uses;
    this._includedEvents = includedEvents;
    this._excludedEvents = excludedEvents;
    this._ticketId = ticketId;
    this._type = type;
  }

  get $typeId(): string {
    return OpenTicketPass.TYPE_ID;
  }

  get code(): string {
    return this.uniqueId;
  }

  get status(): VoucherStatus {
    return this._status;
  }

  get usedAt(): dayjs.Dayjs | null {
    return this._usedAt;
  }

  get maxUses(): number {
    return this._maxUses;
  }

  get uses(): number | null {
    return this._uses;
  }

  get remainingUses(): number {
    return this._maxUses - (this._uses ?? 0);
  }

  get includedEvents(): string[] | null {
    return this._includedEvents;
  }

  get excludedEvents(): string[] | null {
    return this._excludedEvents;
  }

  get ticketId(): string[] | null {
    return this._ticketId;
  }

  get type(): OpenTicketPassType {
    return this._type;
  }

  get $schemaVersion(): VersionRange {
    return VersionRange.between(6, OpenTicketPass.SCHEMA_VERSION);
  }

  static create(opts: {
    uniqueId: string,
    businessRef: BusinessModuleRef,
    creationDate: dayjs.Dayjs,
    orderId: string,
    customerId: string,
    productId: string,
    productPrice: Price,
    components: ComponentList,
    startDate: dayjs.Dayjs | null,
    expirationDate: dayjs.Dayjs | null,
    includedEvents: string[] | null,
    excludedEvents: string[] | null,
    ticketId: string[] | null,
    status: VoucherStatus,
    usedAt: dayjs.Dayjs | null,
    maxUses: number,
    uses: number | null,
    type: OpenTicketPassType,
    serialNumber: string | null,
    userId: string | null,
    isGuestOrder: boolean | null,
    orderEntryId: string | null,
  }): OpenTicketPass {
    return new OpenTicketPass(
        opts.uniqueId,
        opts.businessRef,
        opts.creationDate,
        opts.orderId,
        opts.customerId,
        opts.productId,
        opts.productPrice,
        opts.components,
        opts.startDate,
        opts.expirationDate,
        opts.serialNumber,
        opts.userId,
        opts.isGuestOrder,
        opts.orderEntryId,
        opts.includedEvents,
        opts.excludedEvents,
        opts.ticketId,
        opts.status,
        opts.usedAt,
        opts.maxUses,
        opts.uses,
        opts.type,
    );
  }

  static fromProductOfOrder(
      order: CustomerOrderBase,
      product: Product,
      options: { orderEntryId: string | null },
  ): OpenTicketPass | null {
    const component = product.findComponentByTypeId<OpenTicketPassComponent>(OpenTicketPassComponent.TYPE_ID);
    if(isNullOrUndefined(component) || isNullOrUndefined(component.type) || isNullOrUndefined(order.customerId)) {
      return null;
    }

    const componentList = new ComponentList([]);

    const attachmentComponent = product.findComponentByTypeId(
        VoucherAttachmentComponent.TYPE_ID,
    );

    if(isNotNullOrUndefined(attachmentComponent)) {
      componentList.addComponent(attachmentComponent.clone());
    }

    return this.create({
      uniqueId: OpenTicketPass.generatePromoCode(),
      businessRef: order.businessRef.clone(),
      creationDate: TrueTime.now(),
      orderId: order.uniqueId,
      customerId: order.customerId,
      productId: product.uniqueId,
      productPrice: product.price.clone(),
      components: componentList,
      startDate: component.startDate,
      expirationDate: component.expirationDate,
      serialNumber: null,
      includedEvents: cloneListOrNull(component.includedEvents),
      excludedEvents: cloneListOrNull(component.excludedEvents),
      ticketId: cloneListOrNull(component.ticketId),
      status: VoucherStatus.Live,
      usedAt: null,
      maxUses: component.maxUses,
      uses: null,
      type: component.type,
      userId: order.userId,
      isGuestOrder: order.userId != null ? order.isGuestOrder : null,
      orderEntryId: options.orderEntryId,
    });
  }

  static fromMap(context: SerializationContext, map: SerializedData): OpenTicketPass {
    return OpenTicketPass.create({
      uniqueId: MapUtils.getString(map, 'uniqueId'),
      businessRef: BusinessModuleRef.mustDecode(MapUtils.getString(map, 'businessRef')),
      creationDate: TimeZone.decodeDate(MapUtils.getNumber(map, 'creationDate')),
      orderId: MapUtils.getString(map, 'orderId'),
      customerId: MapUtils.getString(map, 'customerId'),
      productId: MapUtils.getString(map, 'productId'),
      productPrice: Price.decode(MapUtils.getString(map, 'productPrice')),
      components: ComponentList.deserialize(
          context, MapUtils.getList<SerializedData>(map, 'components', (data) => JSONUtils.decodeRecord(data)),
      ),
      startDate: TimeZone.decodeDateOrNull(MapUtils.getNumberOrNull(map, 'startDate')),
      expirationDate: TimeZone.decodeDateOrNull(MapUtils.getNumberOrNull(map, 'expirationDate')),
      serialNumber: MapUtils.getStringOrNull(map, 'serialNumber'),
      userId: MapUtils.getStringOrNull(map, 'userId'),
      isGuestOrder: MapUtils.getBooleanOrNull(map, 'isGuestOrder'),
      includedEvents: MapUtils.getListOrNull<string>(map, 'includedEvents', (e) => JSONUtils.decodeString(e)),
      excludedEvents: MapUtils.getListOrNull<string>(map, 'excludedEvents', (e) => JSONUtils.decodeString(e)),
      ticketId: MapUtils.getListOrNull<string>(map, 'ticketId', (e) => JSONUtils.decodeString(e)),
      status: toEnumFromString(MapUtils.getString(map, '$status'), VoucherStatus),
      usedAt: TimeZone.decodeDateOrNull(MapUtils.getNumberOrNull(map, 'usedAt')),
      maxUses: MapUtils.getNumber(map, 'maxUses'),
      uses: MapUtils.getNumberOrNull(map, 'uses'),
      type: toEnumFromString(MapUtils.getString(map, 'type'), OpenTicketPassType),
      orderEntryId: MapUtils.getStringOrNull(map, 'orderEntryId'),
    });
  }

  toMap(context: SerializationContext): SerializedData {
    return {
      'uniqueId': this.uniqueId,
      'code': this.code,
      'businessRef': this.businessRef.encode(),
      'creationDate': TimeZone.encodeDate(this.creationDate),
      'orderId': this.orderId,
      'customerId': this.customerId,
      'productId': this.productId,
      'productPrice': this.productPrice.encode(),
      'startDate': TimeZone.encodeDateOrNull(this.startDate),
      'expirationDate': TimeZone.encodeDateOrNull(this.expirationDate),
      'serialNumber': this.serialNumber,
      'userId': this.userId,
      'isGuestOrder': this.isGuestOrder,
      'orderEntryId': this.orderEntryId,
      '$status': fromEnumAsString(this.status),
      'usedAt': TimeZone.encodeDateOrNull(this.usedAt),
      'includedEvents': this.includedEvents,
      'excludedEvents': this.excludedEvents,
      'ticketId': this.ticketId,
      'maxUses': this.maxUses,
      'uses': this.uses,
      'components': this.components.serialize(context),
      'type': fromEnumAsString(this.type),
    };
  }

  clone(): this {
    return this.copyWith();
  }

  isSame(other: this): boolean {
    if(other === this) {
      return true;
    }

    return other.uniqueId === this.uniqueId &&
        isSameData(other.businessRef, this.businessRef) &&
        other.creationDate === this.creationDate &&
        other.orderId === this.orderId &&
        other.customerId === this.customerId &&
        other.productId === this.productId &&
        other.status === this.status &&
        other.usedAt === this.usedAt &&
        other.startDate === this.startDate &&
        other.expirationDate === this.expirationDate &&
        isSameList(other.includedEvents, this.includedEvents) &&
        isSameList(other.excludedEvents, this.excludedEvents) &&
        isSameList(other.ticketId, this.ticketId) &&
        isSameData(other.productPrice, this.productPrice) &&
        other.maxUses === this.maxUses &&
        other.uses === this.uses &&
        other.type === this.type &&
        other.serialNumber === this.serialNumber &&
        other.userId === this.userId &&
        other.isGuestOrder === this.isGuestOrder &&
        other.orderEntryId === this.orderEntryId &&
        other.components.isSame(this.components);
  }

  copyWith({
    uniqueId = this.uniqueId,
    businessRef = cloneData(this.businessRef),
    creationDate = TimeZone.cloneDate(this.creationDate),
    orderId = this.orderId,
    customerId = this.customerId,
    productId = this.productId,
    productPrice = this.productPrice,
    components = this.components.clone(),
    startDate = TimeZone.cloneDateOrNull(this.startDate),
    expirationDate = TimeZone.cloneDateOrNull(this.expirationDate),
    serialNumber = this.serialNumber,
    userId = this.userId,
    isGuestOrder = this.isGuestOrder,
    orderEntryId = this.orderEntryId,
    includedEvents = cloneListOrNull(this.includedEvents),
    excludedEvents = cloneListOrNull(this.excludedEvents),
    ticketId = cloneListOrNull(this.ticketId),
    status = this.status,
    usedAt = TimeZone.cloneDateOrNull(this.usedAt),
    maxUses = this.maxUses,
    uses = this.uses,
    type = this.type,
  }: {
    uniqueId?: string,
    businessRef?: BusinessModuleRef,
    creationDate?: dayjs.Dayjs,
    orderId?: string,
    customerId?: string,
    productId?: string,
    productPrice?: Price,
    components?: ComponentList,
    startDate?: dayjs.Dayjs | null,
    expirationDate?: dayjs.Dayjs | null,
    serialNumber?: string | null,
    userId?: string | null,
    isGuestOrder?: boolean | null,
    orderEntryId?: string | null,
    includedEvents?: string[] | null,
    excludedEvents?: string[] | null,
    ticketId?: string[] | null,
    status?: VoucherStatus,
    usedAt?: dayjs.Dayjs | null,
    maxUses?: number,
    uses?: number | null,
    type?: OpenTicketPassType,
  } = {}): this {
    return OpenTicketPass.create({
      uniqueId,
      businessRef,
      creationDate,
      orderId,
      customerId,
      productId,
      productPrice,
      components,
      startDate,
      expirationDate,
      serialNumber,
      userId,
      isGuestOrder,
      orderEntryId,
      includedEvents,
      excludedEvents,
      ticketId,
      status,
      usedAt,
      maxUses,
      uses,
      type,
    }) as this;
  }

  copyAsCanceled(isRefund: boolean): this {
    return this.copyWith({
      status: isRefund ? VoucherStatus.Refunded : VoucherStatus.Canceled,
    });
  }

  updateUsage(discount: Discount, now?: dayjs.Dayjs): void {
    if(this.status !== VoucherStatus.Live) {
      return;
    }

    this._uses = Math.min(this._maxUses, (this._uses ?? 0) + 1);
    if(this._uses >= this._maxUses) {
      this._usedAt = now ?? TrueTime.now();
      this._status = VoucherStatus.Used;
    }
  }

  tryUpdateUsage(discount: Discount, now: dayjs.Dayjs): Result<this, ErrorLike> {
    const self = this.clone();

    if(self.status !== VoucherStatus.Live) {
      return Result.error(new Error(`Voucher status {${self.status}} is not ${VoucherStatus.Live}.`));
    }

    self._uses = Math.min(self._maxUses, (self._uses ?? 0) + 1);
    if(self._uses >= self._maxUses) {
      self._usedAt = now;
      self._status = VoucherStatus.Used;
    }

    return Result.ok(self);
  }

  includeEvents(events: string[]): void {
    if(this._includedEvents == null) {
      this._includedEvents = [...events];
    }
    else {
      for(const eventId of events) {
        if(!this._includedEvents.includes(eventId)) {
          this._includedEvents.push(eventId);
        }
      }
    }
  }

  excludeEvents(events: string[]): void {
    if(this._excludedEvents == null) {
      this._excludedEvents = [...events];
    }
    else {
      for(const eventId of events) {
        if(!this._excludedEvents.includes(eventId)) {
          this._excludedEvents.push(eventId);
        }
      }
    }
  }

  setSerialNumber(serialNumber: string): this {
    if(this.serialNumber != null) {
      throw new IllegalOperationException(`Cannot set serial number {${serialNumber}} for Open Ticket Pass {${this.uniqueId}} of business {${this.businessRef}} because it already has a serial number {${this.serialNumber}}.`);
    }

    return this.copyWith({
      serialNumber: serialNumber,
    });
  }

  static generatePromoCode(): string {
    const randomStr = generateUniqueDatabaseId({ length: 15, alphabet: 'NONSIMILAR_CHARACTERS' });
    return `OP-${randomStr.substring(0, 5)}-${randomStr.substring(5, 10)}-${randomStr.substring(10, randomStr.length)}`;
  }

  static verifyType(dm: DataModel): boolean {
    return dm instanceof OpenTicketPass;
  }

  static assertType(dm: DataModel): dm is OpenTicketPass {
    return dm instanceof OpenTicketPass;
  }
}

export class OpenTicketPassBuilder extends DataModelBuilder<OpenTicketPass> {
  build(context: SerializationContext, map: SerializedData): OpenTicketPass {
    return OpenTicketPass.fromMap(context, map);
  }

  migrateToCurrentSchemaVersion(context: SerializationContext, map: SerializedData): SerializedData | null {
    const versionRange = MapUtils.getVersionRange(map);
    if(versionRange.contains(OpenTicketPass.SCHEMA_VERSION)) {
      return map;
    }
    if(versionRange.isGreaterThan(OpenTicketPass.SCHEMA_VERSION)) {
      return null; // This data comes from the FUTURE. We have no idea how to migrate it.
    }

    let version = versionRange.to;

    // In this version we added `productPrice` of type `Price`.
    if(version < 2) {
      const productPrice = context.storage.get<Price>('productPrice');
      if(productPrice == null) {
        return null; // This data is deprecated and we can't migrate it.
      }

      map['productPrice'] = productPrice.encode();
      version = 2;
    }

    // In this version we added `type` of type `OpenTicketPassType`
    if(version < 3) {
      const productPrice = Price.decode(MapUtils.get<string>(map, 'productPrice'));

      let type = OpenTicketPassType.Standard;
      if(productPrice.value <= 0) {
        type = OpenTicketPassType.Protocol;
      }

      map['type'] = fromEnumAsString(type);
      version = 3;
    }

    /**
     * Version 4:
     *  - parameter `startDate` type {@link dayjs.Dayjs | null} introduced.
     */
    if(version < 4) version = 4;

    /**
     * Version 5:
     * - add `serialNumber` field of type {@link string | null}.
     */
    if(version < 5) version = 5;

    if(version < 6) {
      map['includedEvents'] = map['eventId'];
      version = 6;
    }

    if(version < 7) version = 7;

    /**
     * Version 8:
     *  - parameter `userId` type {string | null} was introduced.
     *  - parameter `isGuestOrder` type {boolean | null} was introduced.
     */
    if(version < 8) version = 8;

    /**
     * Version 9:
     *  - parameter `orderEntryId` type {string | null} was introduced.
     */
    if(version < 9) version = 9;

    map['$schema_version'] = VersionRange.encodedExactly(version);
    return map;
  }
}
